import { MailOutlined, SettingOutlined } from '@ant-design/icons'
import FormCard from '@components/FormCard'
import { useHasAccess } from '@components/HasAccess'
import Page from '@components/Page/Page'
import { Platform } from '@components/platform/Platform'
import { Venue } from '@components/venue/Venue'
import { WhatsappBotWABAWithConfiguration } from '@models/WhatsappBot'
import { useTenant } from '@modules/tenant/contexts/tenant/TenantContext'
import { fetchVenueWhatsappBotDetailsBySlug } from '@services/VenueService'
import { useAppSelector } from '@store/hooks'
import { selectSelectedVenue } from '@store/VenueReducer'
import { SBRMAction } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import Feature from '@components/Feature'
import { useFeature } from '@utils/featureFlags'
import { Permission } from '@utils/permissions'
import { Alert, Button, Col, Space, Spin, Typography } from 'antd'
import { useEffect, useState } from 'react'

const { Title } = Typography

const BookingTab = () => {
  const { allPlatforms, tenant } = useTenant()

  const { hasAccess } = useHasAccess()
  // Presence is decided here (a gate that renders nothing would still leave an
  // empty item in the Space); the gates below frame a preview.
  const { enabled: externalBookingApi } = useFeature('externalBookingApi')
  const { enabled: webBooking } = useFeature('webBooking')

  const venue = useAppSelector(selectSelectedVenue())
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [whatsappBotDetails, setWhatsappBotDetails] = useState<
    WhatsappBotWABAWithConfiguration | undefined
  >(undefined)

  const fetchData = () => {
    if (!venue) return
    setIsLoading(true)
    fetchVenueWhatsappBotDetailsBySlug(venue.slug)
      .then((response) => {
        setWhatsappBotDetails(response.data)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  useEffect(() => {
    fetchData()
  }, [venue?.id])

  if (!venue) {
    return <Spin />
  }

  return (
    <Space orientation="vertical" size={32}>
      <Platform.BookingCard
        enable={venue.booking.app.enable}
        title="Mobile application"
        venue={venue}
        enableBookingFormSections={['booking-application']}
        demoData={{
          title: 'Make your venue bookable in the app',
          description:
            'Enable bookings directly from the app by and select your booking method.',
          imageUrl: '/assets/appDemo.png',
          action: (
            <FormCard
              autoSize
              noCardWrap
              saveOnChange
              entity={Entity.venue}
              action={SBRMAction.Update}
              sections={['booking-application']}
              disabled={!hasAccess(Permission.updateVenue)}
            />
          ),
        }}
        availableConnectors={[
          'sevenrooms',
          'intercom',
          'bookingLink',
          'phone',
          'whatsapp',
          'membershipChat',
          'email',
        ]}
        product="app"
      />
      <Platform.BookingCard
        enable={whatsappBotDetails?.waba != undefined}
        title="WhatsApp bot"
        venue={venue}
        enableBookingFormSections={['booking-whatsapp']}
        demoData={{
          title: 'Connect to WhatsApp',
          description:
            'Connect your WhatsApp Business Account to Pléthore to enable chatbot reservation module.',
          imageUrl: '/assets/whatsappDemo.png',
          action: (
            <Venue.WhatsappEmbeddedSignup
              registerSuccessCallback={fetchData}
              whatsappBotDetails={whatsappBotDetails}
            />
          ),
        }}
        footer={
          venue?.booking.chatbot.connectors.restaurant !== null && (
            <>
              <Alert
                title="You can customize the messages sent by the bot to match your brand and align with your concept."
                type="success"
                action={
                  <Button
                    href={`/venues/${venue.slug}/whatsapp-settings`}
                    type="primary"
                    icon={<SettingOutlined />}
                  >
                    Edit settings & texts
                  </Button>
                }
              />
              <Alert
                showIcon
                title="You can enable the automatic generation of SevenRooms payment links and have them sent directly in the conversation."
                type="warning"
                action={
                  <FormCard
                    saveOnChange
                    noCardWrap
                    entity={Entity.venue}
                    action={SBRMAction.Update}
                    sections={['booking-whatsapp-paymentLink']}
                    disabled={!hasAccess(Permission.updateVenue)}
                  />
                }
              />
            </>
          )
        }
        availableConnectors={['sevenrooms']}
        product="chatbot"
      />
      {tenant.name === 'Paris Society' && (
        <Platform.BookingCard
          enable={venue.booking.companyBackOffice?.enable}
          title="Business module"
          venue={venue}
          enableBookingFormSections={['booking-company-back-office']}
          demoData={{
            title: 'Business module',
            description:
              'Turn your venue into a bookable space straight from the Business module.',
            imageUrl: '/assets/businessDemo.png',
            action: (
              <FormCard
                autoSize
                noCardWrap
                saveOnChange
                entity={Entity.venue}
                action={SBRMAction.Update}
                sections={['booking-company-back-office']}
                disabled={!hasAccess(Permission.updateVenue)}
              />
            ),
          }}
          availableConnectors={['sevenrooms']}
          product="company_back_office"
        />
      )}
      {externalBookingApi && (
        <Feature flag="externalBookingApi" preview="section">
          <Platform.BookingCard
            enable={venue.booking.externalBookingAPI?.enable}
            title="External Booking API"
            venue={venue}
            enableBookingFormSections={['booking-external-api']}
            demoData={{
              title: 'External Booking API',
              description:
                'Turn your venue into a bookable space straight from the External Booking API.',
              imageUrl: '/assets/externalApiDemo.png',
              action: (
                <FormCard
                  autoSize
                  noCardWrap
                  saveOnChange
                  entity={Entity.venue}
                  action={SBRMAction.Update}
                  sections={['booking-external-api']}
                  disabled={!hasAccess(Permission.updateVenue)}
                />
              ),
            }}
            availableConnectors={['sevenrooms']}
            product="external_booking_api"
          />
        </Feature>
      )}
      {webBooking && (
        <Feature flag="webBooking" preview="section">
          <Platform.BookingCard
            enable={venue.booking.web?.enable}
            title="Website"
            venue={venue}
            enableBookingFormSections={['booking-website']}
            demoData={{
              title: 'Website',
              description:
                'Turn your venue into a bookable space straight from your website.',
              imageUrl: '/assets/websiteDemo.svg',
              action: (
                <FormCard
                  autoSize
                  noCardWrap
                  saveOnChange
                  entity={Entity.venue}
                  action={SBRMAction.Update}
                  sections={['booking-website']}
                  disabled={!hasAccess(Permission.updateVenue)}
                />
              ),
            }}
            availableConnectors={['sevenrooms']}
            product="web"
          />
        </Feature>
      )}
      <Page noWrapWithCol>
        <Col span={12}>
          <FormCard
            title="Group booking email"
            icon={
              <Space>
                <Title level={5} className="m-0">
                  {allPlatforms.ios.icon} {allPlatforms.android.icon}{' '}
                  {allPlatforms.whatsapp.icon}
                </Title>
              </Space>
            }
            entity={Entity.venue}
            action={SBRMAction.Update}
            description="This email is displayed when a user click on the +8 button"
            sections={['bookingEmail']}
            disabled={!hasAccess(Permission.updateVenue)}
          />
        </Col>
        <Col span={12}>
          <FormCard
            title="Note to guests in emails"
            icon={<MailOutlined />}
            entity={Entity.venue}
            action={SBRMAction.Update}
            description="This content will be added to the bottom of the email sent to guests when they create, update or cancel a booking."
            sections={['bookingNotification']}
            disabled={!hasAccess(Permission.updateVenue)}
          />
        </Col>
      </Page>
    </Space>
  )
}

export default BookingTab
